import { useEffect, useRef, type ReactNode } from 'react'
import { X } from 'lucide-react'

/** Local dialog with focus containment, Escape dismissal, and focus restoration. */
export function PartyDialog({
  title,
  onClose,
  children,
}: {
  title: string
  onClose: () => void
  children: ReactNode
}) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const prior = document.activeElement as HTMLElement | null
    ref.current?.focus()
    return () => prior?.focus()
  }, [])
  return (
    <div
      className="pi-dialog-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        ref={ref}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="pi-dialog"
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.stopPropagation()
            onClose()
          }
          if (event.key === 'Tab') {
            const focusable = Array.from(
              ref.current?.querySelectorAll<HTMLElement>(
                'button:not(:disabled), a[href], input, select, textarea, [tabindex="0"]',
              ) ?? [],
            )
            const first = focusable[0],
              last = focusable[focusable.length - 1]
            if (
              event.shiftKey &&
              (document.activeElement === first ||
                document.activeElement === ref.current)
            ) {
              event.preventDefault()
              last?.focus()
            } else if (!event.shiftKey && document.activeElement === last) {
              event.preventDefault()
              first?.focus()
            }
          }
        }}
      >
        <header>
          <h2>{title}</h2>
          <button type="button" aria-label="Close" onClick={onClose}>
            <X size={18} />
          </button>
        </header>
        <div className="pi-dialog-body">{children}</div>
      </div>
    </div>
  )
}
